import moment from 'moment';
import * as React from 'react';

import AutomaticRenewalModal from './automaticRenewalModal';
import TitleAndContent from './current_subscription_title_and_content';
import {
  ACCOUNT_TYPE_TO_SUBSCRIPTION_TYPES,
  CREDIT_CARD,
  getSubscriptionTheme,
  SALESMATE_RENEWAL_FORM_URL,
  SCHOOL_PREMIUM_SCHOLARSHIP,
  TEACHER_PREMIUM_CREDIT,
  TEACHER_PREMIUM_SCHOLARSHIP,
  TEACHER_PREMIUM_TRIAL,
} from './constants';

import { requestPost } from '../../../../modules/request';
import {
  defaultSnackbarTimeout,
  helpIcon,
  Snackbar,
  Tooltip,
} from '../../../Shared/index';
import useSnackbarMonitor from '../../../Shared/hooks/useSnackbarMonitor';

interface SubscriptionStatus {
  account_type: string;
  created_at: string;
  de_activated_date: string | null;
  expiration: string;
  expired?: boolean;
  id: number;
  last_four: string | null;
  mail_to: string;
  payment_amount: number | null;
  payment_method: string;
  purchaser_email: string;
  purchaser_id: string | null;
  purchaser_name: string | null;
  recurring: boolean;
  renewal_price: number;
  start_date: string;
  subscription_type_id: number | null;
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
  updated_at: string;
}

interface CurrentSubscriptionProps {
  authorityLevel?: boolean;
  purchaserNameOrEmail?: string | null;
  subscriptions?: any[];
  subscriptionStatus?: SubscriptionStatus | null;
  subscriptionType: string;
  updateSubscription: (data: { recurring: boolean }, id: number, callback: () => void) => void;
}

const CurrentSubscription = ({ subscriptionType, subscriptionStatus, authorityLevel, purchaserNameOrEmail, updateSubscription, subscriptions, }: CurrentSubscriptionProps) => {
  const [showAutomaticRenewalModal, setShowAutomaticRenewalModal] = React.useState(false)
  const [showSnackbar, setShowSnackbar] = React.useState(false)
  const [snackbarCopy, setSnackbarCopy] = React.useState('')

  useSnackbarMonitor(showSnackbar, setShowSnackbar, defaultSnackbarTimeout)

  const getNextSubscription = () => {
    if (!subscriptions || !subscriptionStatus) { return null }

    const currentExpiration = new Date(subscriptionStatus.expiration)

    return subscriptions.find(sub => {
      if (sub.id === subscriptionStatus.id) { return false }
      const subExpiration = new Date(sub.expiration)
      return subExpiration > currentExpiration && !sub.expired
    })
  }

  const onceYourPlanExpires = () => {
    const { payment_method, purchaser_email, } = subscriptionStatus
    const baseText = `Once your current ${subscriptionType} subscription expires, you will be downgraded to Quill Basic.`;

    if ([TEACHER_PREMIUM_TRIAL, TEACHER_PREMIUM_CREDIT, TEACHER_PREMIUM_SCHOLARSHIP].includes(subscriptionType)) { return baseText }

    if (subscriptionType === SCHOOL_PREMIUM_SCHOLARSHIP && !authorityLevel) { return baseText }

    if (payment_method === CREDIT_CARD && authorityLevel) {
      return `${baseText} To prevent your subscription from expiring, turn on automatic renewal.`
    }
    if (payment_method === CREDIT_CARD && !authorityLevel) {
      return <span>{baseText} To prevent your subscription from expiring, contact the purchaser at <a className="focus-on-light" href={`mailto:${purchaser_email}`}>{purchaser_email}</a> and ask them to turn on automatic renewal.</span>
    }
    if (payment_method !== CREDIT_CARD && (authorityLevel || !purchaser_email)) {
      return <span>{baseText} To renew your subscription for next year, <a className="focus-on-light green-link" href={SALESMATE_RENEWAL_FORM_URL} rel="noopener noreferrer" target="_blank">contact us</a>.</span>
    }
    if (payment_method !== CREDIT_CARD && !authorityLevel) {
      return <span>{baseText} To renew your subscription for next year, contact the purchaser at <a className="focus-on-light green-link" href={`mailto:${purchaser_email}`}>{purchaser_email}</a> or <a href="mailto:sales@quill.org">the Quill team</a>.</span>
    }
    return baseText
  }

  const getPaymentMethod = () => {
    if (subscriptionStatus && subscriptionStatus.payment_method === CREDIT_CARD && authorityLevel) {
      return editCreditCardElement();
    } else if (subscriptionStatus && subscriptionStatus.payment_method === CREDIT_CARD) {
      return <span>Credit Card</span>;
    } else if (subscriptionType === TEACHER_PREMIUM_TRIAL) {
      return <span>No payment method on file</span>;
    } else if (subscriptionStatus && ['Invoice', 'School Invoice'].includes(subscriptionStatus.payment_method)) {
      return <span>Invoice</span>;
    }
    return <span>No payment method on file</span>;
  }

  const getPrice = () => {
    return subscriptionStatus.renewal_price
  }

  const content = () => {
    const metaRowClassName = 'sub-meta-info';
    if (subscriptionStatus) {
      const planTitleContent = (
        <span className="content-wrapper">
          {subscriptionType}
          <Tooltip
            tooltipText={`You have a ${subscriptionStatus.account_type} subscription`}
            tooltipTriggerText={<img alt={helpIcon.alt} className="subscription-tooltip" src={helpIcon.src} />}
          />
        </span>
      )
      let automaticRenewalContent = subscriptionStatus.recurring ? 'On' : 'Off'

      if (authorityLevel && subscriptionStatus.payment_method !== CREDIT_CARD && !subscriptionStatus.recurring) {
        automaticRenewalContent = (
          <span className="content-wrapper">
            Off
            <Tooltip
              tooltipText={`To renew your subscription for next year, contact us at ${SALESMATE_RENEWAL_FORM_URL}.`}
              tooltipTriggerText={<img alt={helpIcon.alt} className="subscription-tooltip" src={helpIcon.src} />}
            />
          </span>
        )
      }

      return ({ metaRows: (
        <div className={metaRowClassName}>
          <div className="meta-section">
            <h3>Current Subscription</h3>
            <dl>
              <TitleAndContent content={planTitleContent} title="Plan" />
              {purchaserNameOrEmail && purchaserNameOrEmail.length && <TitleAndContent content={purchaserNameOrEmail} title="Purchaser" />}
              <TitleAndContent content={moment(subscriptionStatus.start_date).format('MMMM Do, YYYY')} title="Start Date" />
              <TitleAndContent content={moment(subscriptionStatus.expiration).format('MMMM Do, YYYY')} title="End Date" />
              <TitleAndContent content={automaticRenewalContent} title="Automatic Renewal" />
            </dl>
            {renderAutomaticRenewalButton()}
          </div>
          {paymentMethod()}
          {nextPlan()}
        </div>
      ),
      cta: (<span />), });
    }
    // set a more basic state if we don't have the info
    return ({ metaRows: (
      <div className={metaRowClassName}>
        <div className="meta-section">
          <h3>Current Subscription</h3>
          <dl>
            <TitleAndContent content="Quill Basic - Free" title="Plan" />
          </dl>
        </div>
        {paymentMethod()}
        {nextPlan()}
      </div>
    ),
    cta: (
      <div className="sub-button-row">
        <a className="q-button button cta-button bg-orange text-white" href="/premium">Learn More About Quill Premium</a>
        <a className="q-button button cta-button bg-quillblue text-white" href="https://assets.quill.org/documents/quill_premium.pdf" rel="noopener noreferrer" target="_blank"><i className="fas fa-file-pdf" />Download Premium PDF</a>
      </div>
    ), });
  }

  const handleEditCreditCardClick = () => {
    const { stripe_customer_id, stripe_subscription_id } = subscriptionStatus

    if ( !stripe_customer_id ) { return }

    const path = '/stripe_integration/subscription_payment_methods'
    const data = { stripe_customer_id, stripe_subscription_id }

    requestPost(path, data, body => { window.location.replace(body.redirect_url) })
  }

  const editCreditCardElement = () => {
    const { last_four } = subscriptionStatus

    return (
      <div className="credit-card-info-container">
        <span>{`Credit card ending in ${last_four}`}</span>
        <button
          className="quill-button contained green extra-small focus-on-light"
          onClick={handleEditCreditCardClick}
          type="button"
        >Edit credit card
        </button>
      </div>
    );
  }

  const nextPlan = () => {
    if (subscriptionStatus) {
      return (
        <div className="meta-section">
          <h3>Next Subscription</h3>
          {nextPlanContent()}
        </div>
      );
    }
  }

  // Same theme treatment as SubscriptionStatus / AvailableCredits: fall back
  // to the neutral basic-theme when there's no active premium so the alert
  // doesn't render in maroon/gold for an expired user.
  const hasActivePremium = subscriptionStatus && !subscriptionStatus.expired
  const alertThemeClass = hasActivePremium ? getSubscriptionTheme(subscriptionType) : 'basic-theme'

  const nextPlanAlert = (body) => {
    return (
      <div className={`next-plan-alert ${alertThemeClass} flex-row vertically-centered`}>
        {body}
      </div>
    )
  }

  const renewDate = () => {
    return moment(subscriptionStatus.expiration).add('days', 1).format('MMMM Do, YYYY');
  }

  const nextPlanContent = () => {
    if (subscriptionStatus.expired) { return }

    let nextPlan = <span>Quill Basic - Free</span>
    let beginsOn
    // The default "Once your … subscription expires" alert is no longer rendered
    // here — the parent Subscriptions page surfaces it as a sibling card next to
    // SubscriptionStatus on the left. Recurring / queued-successor alerts still
    // render below as they're tied to this section's content.
    let nextPlanAlertOrButtons: React.ReactNode = null

    const nextSubscription = getNextSubscription()

    if (subscriptionStatus.recurring) {
      nextPlan = (<span>{subscriptionType} - ${getPrice()} Annual Subscription</span>);
      nextPlanAlertOrButtons = nextPlanAlert(`Your ${subscriptionType} subscription will be renewed on ${renewDate()} and your card ending in ${subscriptionStatus.last_four} will be charged $${getPrice()}.`);
    } else if (nextSubscription) {
      const nextSubscriptionType = ACCOUNT_TYPE_TO_SUBSCRIPTION_TYPES[nextSubscription.account_type]
      const expirationDate = new Date(subscriptionStatus.expiration)
      expirationDate.setDate(expirationDate.getDate() + 1)
      const nextSubscriptionStartDate = expirationDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
      nextPlan = <span>{nextSubscriptionType}</span>
      nextPlanAlertOrButtons = nextPlanAlert(`Once your current ${subscriptionType} subscription expires, your ${nextSubscriptionType} subscription will begin.`)
    }

    return (
      <div>
        <div className="flex-row space-between">
          <dl>
            <TitleAndContent content={nextPlan} title="Next Plan" />
          </dl>
        </div>
        {nextPlanAlertOrButtons}
      </div>
    );
  }

  const paymentMethod = () => {
    return (
      <div className="meta-section payment">
        <h3>Payment Method on File</h3>
        {getPaymentMethod()}
      </div>
    );
  }

  const handleClickShowAutomaticRenewalModal = () => { setShowAutomaticRenewalModal(true) }

  const handleClickHideAutomaticRenewalModal = () => { setShowAutomaticRenewalModal(false) }

  const handleShowSnackbar = snackbarText => {
    setSnackbarCopy(snackbarText)
    setShowSnackbar(true)
  };

  const handleUpdateRecurringClick = () => {
    const { stripe_subscription_id, recurring, id, } = subscriptionStatus

    const newRecurringValue = !recurring
    const snackbarText = `Automatic renewal turned ${newRecurringValue ? 'on' : 'off'}.` // "recurring" is the old value, so the description is the inverse

    const updateSubscriptionSuccessCallback = () => {
      setShowAutomaticRenewalModal(false)
      handleShowSnackbar(snackbarText)
    }

    if (stripe_subscription_id) {
      const path = '/stripe_integration/subscription_renewals'
      const cancel_at_period_end = !newRecurringValue
      const data = { stripe_subscription_id, cancel_at_period_end }
      const success = () => { updateSubscription({ recurring: newRecurringValue }, id, updateSubscriptionSuccessCallback) }

      const error = () => { alert('An error occurred updating the subscription. Please contact hello@quill.org.') }

      requestPost(path, data, success, error)
    } else {
      updateSubscription({ recurring: newRecurringValue }, id, callback)
    }
  }

  const renderAutomaticRenewalButton = () => {
    if (authorityLevel && subscriptionStatus.payment_method === CREDIT_CARD) {
      return (
        <button
          className="quill-button contained extra-small green focus-on-light"
          onClick={handleClickShowAutomaticRenewalModal}
          type="button"
        >
          {subscriptionStatus.recurring ? 'Turn off automatic renewal' : 'Turn on automatic renewal'}
        </button>
      );
    }
  }

  const renderAutomaticRenewalModal = () => {
    return (
      <AutomaticRenewalModal
        authorityLevel={authorityLevel}
        close={handleClickHideAutomaticRenewalModal}
        handleUpdateRecurringClick={handleUpdateRecurringClick}
        price={getPrice()}
        show={showAutomaticRenewalModal}
        subscriptionStatus={subscriptionStatus}
        subscriptionType={subscriptionType}
      />
    )
  }

  const renderSnackbar = () => {
    return <Snackbar text={snackbarCopy} visible={showSnackbar} />
  }

  if (!subscriptionStatus || subscriptionStatus.expired) { return <span /> }

  return (
    <section aria-labelledby="subscriptionInformationTitle" className="subscription-information">
      {renderAutomaticRenewalModal()}
      <h2 id="subscriptionInformationTitle">Subscription Information</h2>
      <div className="current-subscription-information-and-cta">
        <div className="current-subscription-information">
          {content().metaRows}
        </div>
        {content().cta}
      </div>
      {renderSnackbar()}
    </section>
  );

}

export default CurrentSubscription
